/**
 * Deep links: `?network=noaa&mission=noaa20&antenna=wallops_26m&asset=rf_antenna&t=2026-10-10T12:00Z&tab=gantt&speed=60`
 * applied once on load so any demo state is one URL. The parameters are read,
 * not rewritten, so the address bar stays clean while the user navigates.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useClock, useUI, type Mode } from '@/state/store';
import { useLoader } from '@/features/loader/store';
import { selectTwinCatalogue, useCatalogueView } from '@/features/catalogue/view';

const KNOWN = ['network', 'mission', 'antenna', 'asset', 't', 'tab', 'speed', 'panel'] as const;

export function useDeepLink(mode: Mode): void {
  const [params] = useSearchParams();
  useEffect(() => {
    if (!KNOWN.some((k) => params.has(k))) return;
    const ui = useUI.getState();
    const loader = useLoader.getState();
    const clock = useClock.getState();

    const network = params.get('network');
    if (network && network !== 'demo' && network !== loader.network) loader.setNetwork(network);
    if (network && mode === 'maintenance') useCatalogueView.getState().setExamples(network === 'demo');

    const t = params.get('t');
    if (t) {
      const ms = Date.parse(t);
      if (Number.isFinite(ms)) clock.setSimTime(ms);
      else console.warn(`[deep link] ignored t=${t}: not a date`);
    }
    const speed = params.get('speed');
    if (speed) {
      const m = Number(speed);
      if (Number.isFinite(m) && m > 0) {
        if (clock.live) clock.jump(0);
        clock.setMultiplier(m);
      }
    }

    const mission = params.get('mission');
    const antenna = params.get('antenna');
    const asset = params.get('asset');
    if (mission) ui.select({ missionId: mission, antennaId: null });
    else if (antenna) ui.select({ antennaId: antenna, missionId: null });
    if (asset) {
      ui.select({ assetId: asset });
      if (mode === 'maintenance') selectTwinCatalogue(asset);
    }

    const tab = params.get('tab');
    if (tab) ui.setBottomTab(tab);
    const panel = params.get('panel');
    if (panel === 'closed' && ui.rightPanelOpen) ui.toggleRightPanel();
    // Applied once per page load; later navigation is the user's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
